const normalizeStatus = (status = 'unknown') => String(status || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-');

const asArray = (payload, keys = []) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  const foundKey = keys.find((key) => Array.isArray(payload?.[key]));
  return foundKey ? payload[foundKey] : [];
};

const getRuleFeedId = (rule = {}) => rule.feedId
  || rule.feed_id
  || rule.feed
  || rule.feedUuid
  || rule.feed_uuid
  || rule.cameraFeedId
  || rule.camera_feed_id
  || rule.sourceFeedId
  || rule.source_feed_id
  || '';

const getRuleFeedUrl = (rule = {}) => rule.feedUrl
  || rule.feed_url
  || rule.sourceUrl
  || rule.source_url
  || rule.snapshotUrl
  || rule.snapshot_url
  || rule.url
  || '';

export const normalizeWatchRule = (rule = {}, fallbackFeedId = '') => ({
  id: rule.id || rule.ruleId || rule.rule_id || rule.name || rule.type || 'out-of-hours-object-watch',
  type: rule.type || rule.ruleType || rule.rule_type || rule.name || 'out_of_hours_object_watch',
  label: rule.label || rule.title || rule.displayName || 'Out of hours object watch',
  severity: normalizeStatus(rule.severity || rule.priority || 'critical'),
  enabled: rule.enabled !== false && rule.disabled !== true,
  state: rule.state || rule.status || (rule.enabled === false || rule.disabled ? 'disabled' : 'enabled'),
  schedule: rule.schedule || rule.scheduleLabel || rule.schedule_label || 'Out of hours',
  objects: asArray(rule.objects || rule.objectLabels || rule.object_labels).join(', ') || 'person/object',
  sync: rule.syncStatus || rule.sync_status || rule.lastSyncStatus || rule.last_sync_status || 'reported by node',
  stale: Boolean(rule.stale || rule.cached || rule.cache_stale),
  feedId: String(getRuleFeedId(rule) || fallbackFeedId || ''),
  feedUrl: String(getRuleFeedUrl(rule) || ''),
});

export const normalizeWatchRulePayload = (payload) => asArray(payload, ['rules', 'watchRules', 'watch_rules', 'items', 'results'])
  .map((rule) => normalizeWatchRule(rule));

const normalizeActiveWatchRulePayload = (payload) => {
  const directRules = normalizeWatchRulePayload(payload);
  const groupedRules = asArray(payload, ['feeds', 'feedRules', 'feed_rules', 'cameras'])
    .flatMap((feed) => {
      const feedId = getRuleFeedId(feed) || feed.id || feed.feedID || feed.cameraId || feed.camera_id;
      const feedUrl = getRuleFeedUrl(feed);
      return asArray(feed, ['rules', 'watchRules', 'watch_rules', 'activeRules', 'active_rules'])
        .map((rule) => ({
          ...normalizeWatchRule(rule, feedId),
          feedUrl: String(getRuleFeedUrl(rule) || feedUrl || ''),
        }));
    });

  return [...directRules, ...groupedRules];
};

export const normalizeActiveWatchRuleState = (payload) => ({
  loading: false,
  error: '',
  stale: Boolean(payload?.stale || payload?.cached || payload?.cache_stale),
  source: payload?.source || payload?.source_name || 'server-link',
  rules: normalizeActiveWatchRulePayload(payload),
});

export const buildWatchRuleError = (error) => {
  const status = error?.response?.status;
  if (status === 404) {
    return 'Watch-rule Server Link routes are not available for this feed yet.';
  }
  if (status === 403) {
    return 'This session is not authorized to manage feed watch rules.';
  }
  if (status === 409) {
    const code = error?.response?.data?.code || error?.response?.data?.error || '';
    if (/already|duplicate|active/i.test(code)) {
      return 'Out of hours object watch is already active for this feed. Refresh rules to confirm the active state.';
    }
    return 'Out of hours object watch needs refreshed feed context before retrying.';
  }
  if (status) {
    return `Watch-rule sync returned HTTP ${status}.`;
  }
  return 'Watch-rule sync is unavailable.';
};

export const buildActiveWatchRulesSummary = (activeRuleState = {}, feedRuleStates = {}) => {
  const keyedRules = new Map();
  const addRule = (rule, fallbackFeedId) => {
    if (!rule?.enabled) {
      return;
    }
    const feedId = String(rule.feedId || fallbackFeedId || 'org-active');
    const id = rule.id || rule.type || rule.label || 'watch-rule';
    const key = `${feedId}:${id}`;
    if (!keyedRules.has(key)) {
      keyedRules.set(key, { ...rule, feedId });
    }
  };

  (activeRuleState.rules || []).forEach((rule) => addRule(rule, rule.feedId));
  Object.entries(feedRuleStates || {}).forEach(([feedId, value]) => {
    (value?.rules || []).forEach((rule) => addRule(rule, feedId));
  });

  let state = 'fresh-empty';
  if (activeRuleState.loading) {
    state = 'loading';
  } else if (activeRuleState.source === 'unqueried') {
    state = 'unqueried';
  } else if (activeRuleState.error) {
    state = 'error';
  } else if (activeRuleState.stale) {
    state = 'stale';
  } else if (keyedRules.size > 0) {
    state = 'active';
  }

  return {
    state,
    rules: Array.from(keyedRules.values()),
    error: activeRuleState.error || '',
    stale: Boolean(activeRuleState.stale),
    source: activeRuleState.source || 'server-link',
  };
};
